import React from "react";
import { Link } from "react-router-dom";

export function isCoLivingUnit(unit) {
  return String(unit?.type || "").trim() === "Co-Living";
}

/** Co-Living: blue · Apartment: slate (non–Co-Living units). */
export function UnitTypeBadge({ unit }) {
  const co = isCoLivingUnit(unit);
  return (
    <span
      data-unit-type={co ? "co-living" : "apartment"}
      className={`inline-flex shrink-0 items-center rounded-full border px-2 py-[2px] text-[9px] font-semibold ${
        co
          ? "border-[rgba(91,156,246,0.35)] bg-[rgba(91,156,246,0.22)] text-[#5b9cf6]"
          : "border-[#1c2035] bg-[#252a38] text-[#a8b0c4]"
      }`}
    >
      {co ? "Co-Living" : "Apartment"}
    </span>
  );
}

export function buildMapsQueryFromUnit(unit) {
  if (!unit) return "";
  const street = String(unit.address_line1 ?? unit.street ?? unit.address ?? "").trim();
  const pc = String(unit.postal_code ?? unit.zip ?? "").trim();
  const ort = String(unit.city ?? unit.place ?? "").trim();
  const tail = [pc, ort].filter(Boolean).join(" ");
  if (street && tail) return `${street}, ${tail}`;
  if (street) return street;
  return tail || ort;
}

export function adminUnitPath(unit, fallbackUnitId) {
  const id = String(unit?.id ?? unit?.unitId ?? fallbackUnitId ?? "").trim();
  return id ? `/admin/units/${encodeURIComponent(id)}` : null;
}

function MapsPinIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
    </svg>
  );
}

/**
 * Map pin opens Google Maps; address text links to unit detail. Clicks do not bubble.
 */
export function UnitAddressActions({ unit, fallbackUnitId, displayText, linkClassName = "" }) {
  const path = adminUnitPath(unit, fallbackUnitId);
  const q = buildMapsQueryFromUnit(unit);
  const mapsUrl = q
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`
    : null;
  const text =
    displayText != null && String(displayText).trim() !== ""
      ? displayText
      : buildMapsQueryFromUnit(unit) || "—";

  const linkCls = `min-w-0 break-words text-left text-[#5b9cf6] transition-colors hover:text-[#7ab8ff] hover:underline ${
    linkClassName || "text-[11px]"
  }`;

  return (
    <div className="flex min-w-0 items-start gap-1.5">
      {mapsUrl ? (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-[2px] shrink-0 text-[#5b9cf6] opacity-85 transition-opacity hover:opacity-100"
          onClick={(e) => e.stopPropagation()}
          aria-label="In Google Maps öffnen"
        >
          <MapsPinIcon className="h-3.5 w-3.5" />
        </a>
      ) : null}
      {path ? (
        <Link to={path} onClick={(e) => e.stopPropagation()} className={linkCls}>
          {text}
        </Link>
      ) : (
        <span
          className={`min-w-0 break-words text-[#8892b0] ${linkClassName || "text-[11px]"}`}
        >
          {text}
        </span>
      )}
    </div>
  );
}
